/**
 * Console Log Capture
 *
 * Intercepts console.log/warn/error and global error events into a ring buffer.
 * Used by the bug report feature to attach diagnostics.
 */

export interface CapturedLog {
  level: "log" | "warn" | "error" | "unhandled";
  message: string;
  timestamp: string;
  stack?: string;
}

const MAX_ENTRIES = 100;
const buffer: CapturedLog[] = [];

function push(entry: CapturedLog) {
  buffer.push(entry);
  if (buffer.length > MAX_ENTRIES) {
    buffer.shift();
  }
}

function argsToString(args: unknown[]): string {
  return args
    .map((a) => {
      if (typeof a === "string") return a;
      try {
        return JSON.stringify(a);
      } catch {
        return String(a);
      }
    })
    .join(" ");
}

let installed = false;

export function installConsoleCapture() {
  if (installed) return;
  installed = true;

  const origLog = console.log.bind(console);
  const origWarn = console.warn.bind(console);
  const origError = console.error.bind(console);

  console.log = (...args: unknown[]) => {
    push({ level: "log", message: argsToString(args), timestamp: new Date().toISOString() });
    origLog(...args);
  };

  console.warn = (...args: unknown[]) => {
    push({ level: "warn", message: argsToString(args), timestamp: new Date().toISOString() });
    origWarn(...args);
  };

  console.error = (...args: unknown[]) => {
    push({ level: "error", message: argsToString(args), timestamp: new Date().toISOString() });
    origError(...args);
  };

  window.addEventListener("error", (event) => {
    push({
      level: "unhandled",
      message: event.message || "Unknown error",
      timestamp: new Date().toISOString(),
      stack: event.error?.stack,
    });
  });

  window.addEventListener("unhandledrejection", (event) => {
    const reason = event.reason;
    push({
      level: "unhandled",
      message: reason?.message || String(reason) || "Unhandled promise rejection",
      timestamp: new Date().toISOString(),
      stack: reason?.stack,
    });
  });
}

export function getConsoleLogs(): CapturedLog[] {
  return [...buffer];
}
